import { useState, useCallback, useRef, useEffect } from 'react';
import { useProjectContext } from '../../contexts/ProjectContext';
import { useExtractDocument, useBulkImport } from '../../hooks';
import { formatCurrency } from '../../utils/pricing';
import type { ExtractedLavoro, CmeLineItem } from '../../types';

interface ImportDocumentWizardProps {
  onClose: () => void;
}

interface EditableLavoro {
  nome: string;
  descrizione: string;
  selected: boolean;
  voci: EditableVoce[];
}

interface EditableVoce extends CmeLineItem {
  selected: boolean;
}

const STEPS = ['Upload', 'Analisi', 'Revisione'] as const;
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ACCEPTED_TYPES: Record<string, string> = {
  'application/pdf': '.pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
};
const ACCEPT_STRING = '.pdf,.docx';

export function ImportDocumentWizard({ onClose }: ImportDocumentWizardProps) {
  const { currentProject } = useProjectContext();
  const extractMutation = useExtractDocument();
  const importMutation = useBulkImport();

  const [step, setStep] = useState(0);
  const [editableLavori, setEditableLavori] = useState<EditableLavoro[]>([]);
  const [fileData, setFileData] = useState<{ data: string; mediaType: string; filename: string } | null>(null);
  const [context, setContext] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [expandedLavoro, setExpandedLavoro] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const getMediaType = (file: File): string | null => {
    // Check by MIME type first
    if (ACCEPTED_TYPES[file.type]) return file.type;
    // Fallback: check by extension
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (ext === 'pdf') return 'application/pdf';
    if (ext === 'docx') return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    return null;
  };

  const handleFileSelect = (file: File) => {
    const mediaType = getMediaType(file);
    if (!mediaType) {
      alert('Formato non supportato. Usa PDF o DOCX.');
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      alert('File troppo grande. Massimo 10MB.');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const base64 = result.split(',')[1];
      setFileData({ data: base64, mediaType, filename: file.name });
    };
    reader.readAsDataURL(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  };

  const handleExtract = useCallback(() => {
    if (!currentProject || !fileData) return;

    extractMutation.mutate(
      {
        projectId: currentProject.id,
        file: fileData,
        context: context || undefined,
      },
      {
        onSuccess: (result) => {
          setEditableLavori(
            result.lavori.map((lavoro: ExtractedLavoro) => ({
              nome: lavoro.nome,
              descrizione: lavoro.descrizione,
              selected: true,
              voci: lavoro.voci.map((voce) => ({ ...voce, selected: true })),
            }))
          );
          setExpandedLavoro(0);
          setStep(2);
        },
      }
    );
  }, [currentProject, fileData, context, extractMutation]);

  useEffect(() => {
    if (step === 1 && !extractMutation.isPending && !extractMutation.isSuccess) {
      handleExtract();
    }
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleLavoro = (index: number) => {
    setEditableLavori((prev) =>
      prev.map((l, i) =>
        i === index
          ? { ...l, selected: !l.selected, voci: l.voci.map((v) => ({ ...v, selected: !l.selected })) }
          : l
      )
    );
  };

  const toggleVoce = (lavoroIndex: number, voceIndex: number) => {
    setEditableLavori((prev) =>
      prev.map((l, li) =>
        li === lavoroIndex
          ? {
              ...l,
              voci: l.voci.map((v, vi) => (vi === voceIndex ? { ...v, selected: !v.selected } : v)),
            }
          : l
      )
    );
  };

  const updateVoceCost = (
    lavoroIndex: number,
    voceIndex: number,
    field: 'costoMin' | 'costoMed' | 'costoMax',
    value: string
  ) => {
    setEditableLavori((prev) =>
      prev.map((l, li) =>
        li === lavoroIndex
          ? {
              ...l,
              voci: l.voci.map((v, vi) =>
                vi === voceIndex ? { ...v, [field]: parseFloat(value) || 0 } : v
              ),
            }
          : l
      )
    );
  };

  const updateLavoroNome = (index: number, nome: string) => {
    setEditableLavori((prev) =>
      prev.map((l, i) => (i === index ? { ...l, nome } : l))
    );
  };

  // Summary calculations
  const selectedLavori = editableLavori.filter((l) => l.selected);
  const allSelectedVoci = selectedLavori.flatMap((l) => l.voci.filter((v) => v.selected));
  const totals = allSelectedVoci.reduce(
    (acc, v) => ({
      min: acc.min + v.costoMin,
      med: acc.med + v.costoMed,
      max: acc.max + v.costoMax,
    }),
    { min: 0, med: 0, max: 0 }
  );

  const handleSave = () => {
    if (!currentProject) return;

    const lavori = selectedLavori
      .filter((l) => l.voci.some((v) => v.selected))
      .map((l) => ({
        nome: l.nome,
        descrizione: l.descrizione || undefined,
        voci: l.voci
          .filter((v) => v.selected)
          .map((v) => ({
            categoria: v.categoria,
            voce: v.voce,
            costoMin: v.costoMin.toFixed(2),
            costoMed: v.costoMed.toFixed(2),
            costoMax: v.costoMax.toFixed(2),
            note: v.note || undefined,
          })),
      }));

    if (lavori.length === 0) return;

    importMutation.mutate(
      { projectId: currentProject.id, lavori },
      { onSuccess: () => onClose() }
    );
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-auto animate-slide-up">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Importa Documento CME</h2>
              <p className="text-sm text-gray-500 mt-0.5">
                Carica un CME, relazione o capitolato per estrarre automaticamente i lavori
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              disabled={extractMutation.isPending}
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Step Indicator */}
          <div className="flex items-center gap-2 mb-6">
            {STEPS.map((label, i) => (
              <div key={label} className="flex items-center flex-1">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium shrink-0 ${
                    i === step
                      ? 'bg-primary-600 text-white'
                      : i < step
                        ? 'bg-primary-100 text-primary-700'
                        : 'bg-gray-100 text-gray-400'
                  }`}
                >
                  {i < step ? (
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    i + 1
                  )}
                </div>
                {i < STEPS.length - 1 && (
                  <div className={`h-0.5 flex-1 mx-1 ${i < step ? 'bg-primary-300' : 'bg-gray-200'}`} />
                )}
              </div>
            ))}
          </div>
          <p className="text-sm text-gray-500 mb-4">{STEPS[step]}</p>

          {/* Step 0: Upload */}
          {step === 0 && (
            <div className="space-y-4">
              <div
                className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer ${
                  dragOver
                    ? 'border-primary-400 bg-primary-50'
                    : fileData
                      ? 'border-green-400 bg-green-50'
                      : 'border-gray-300 hover:border-primary-400 hover:bg-gray-50'
                }`}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ACCEPT_STRING}
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleFileSelect(file);
                  }}
                />
                {fileData ? (
                  <>
                    <svg className="mx-auto h-10 w-10 text-green-500 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <p className="text-sm font-medium text-green-700">{fileData.filename}</p>
                    <p className="text-xs text-green-600 mt-1">Clicca per cambiare file</p>
                  </>
                ) : (
                  <>
                    <svg className="mx-auto h-10 w-10 text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <p className="text-sm font-medium text-gray-700">
                      Trascina un documento o clicca per selezionare
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      PDF o DOCX - Max 10MB
                    </p>
                    <p className="text-xs text-gray-400 mt-2">
                      Supporta CME, relazioni tecniche, capitolati, preventivi
                    </p>
                  </>
                )}
              </div>

              <div className="input-group">
                <label htmlFor="import-context" className="input-label">
                  Contesto aggiuntivo (opzionale)
                </label>
                <textarea
                  id="import-context"
                  value={context}
                  onChange={(e) => setContext(e.target.value)}
                  className="w-full"
                  rows={2}
                  placeholder="es. Ristrutturazione appartamento 80mq, zona Milano..."
                />
              </div>
            </div>
          )}

          {/* Step 1: Analisi (loading) */}
          {step === 1 && (
            <div className="flex flex-col items-center justify-center py-12">
              {extractMutation.isPending && (
                <>
                  <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin mb-4" />
                  <p className="text-gray-700 font-medium">Estrazione in corso...</p>
                  <p className="text-sm text-gray-500 mt-1">
                    {"L'AI sta analizzando il documento e identificando i lavori"}
                  </p>
                  <p className="text-xs text-gray-400 mt-3">
                    Questo potrebbe richiedere fino a 1-2 minuti per documenti complessi
                  </p>
                </>
              )}
              {extractMutation.isError && (
                <div className="text-center">
                  <div className="w-12 h-12 rounded-full bg-danger-100 flex items-center justify-center mx-auto mb-4">
                    <svg className="w-6 h-6 text-danger-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </div>
                  <p className="text-gray-700 font-medium">{"Errore nell'estrazione"}</p>
                  <p className="text-sm text-danger-600 mt-1">
                    {extractMutation.error?.message || 'Errore sconosciuto'}
                  </p>
                  <button
                    onClick={() => {
                      extractMutation.reset();
                      setStep(0);
                    }}
                    className="btn-secondary mt-4"
                  >
                    Torna indietro
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Revisione */}
          {step === 2 && (
            <div className="space-y-4">
              {/* Summary bar */}
              <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-medium text-primary-800">
                    Estratti {editableLavori.length} lavori con {allSelectedVoci.length} voci selezionate
                  </h4>
                </div>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-xs text-primary-600">Min</p>
                    <p className="text-sm font-semibold text-primary-900">{formatCurrency(totals.min)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-primary-600">Medio</p>
                    <p className="text-sm font-semibold text-primary-900">{formatCurrency(totals.med)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-primary-600">Max</p>
                    <p className="text-sm font-semibold text-primary-900">{formatCurrency(totals.max)}</p>
                  </div>
                </div>
              </div>

              {/* Lavori list */}
              <div className="space-y-3 max-h-[50vh] overflow-y-auto">
                {editableLavori.map((lavoro, lavoroIdx) => {
                  const lavoroVociSelected = lavoro.voci.filter((v) => v.selected);
                  const lavoroTotal = lavoroVociSelected.reduce((s, v) => s + v.costoMed, 0);
                  const isExpanded = expandedLavoro === lavoroIdx;

                  return (
                    <div
                      key={lavoroIdx}
                      className={`border rounded-lg overflow-hidden transition-colors ${
                        lavoro.selected ? 'border-primary-200 bg-white' : 'border-gray-200 bg-gray-50 opacity-60'
                      }`}
                    >
                      {/* Lavoro header */}
                      <div className="flex items-center gap-3 p-3">
                        <input
                          type="checkbox"
                          checked={lavoro.selected}
                          onChange={() => toggleLavoro(lavoroIdx)}
                          className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 shrink-0"
                        />
                        <div
                          className="flex-1 min-w-0 cursor-pointer"
                          onClick={() => setExpandedLavoro(isExpanded ? null : lavoroIdx)}
                        >
                          <div className="flex items-center gap-2">
                            <svg
                              className={`w-4 h-4 text-gray-400 transition-transform shrink-0 ${isExpanded ? 'rotate-90' : ''}`}
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                            <input
                              type="text"
                              value={lavoro.nome}
                              onChange={(e) => updateLavoroNome(lavoroIdx, e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                              className="text-sm font-semibold text-gray-900 bg-transparent border-0 border-b border-transparent hover:border-gray-300 focus:border-primary-500 p-0 w-full focus:ring-0"
                              disabled={!lavoro.selected}
                            />
                          </div>
                          {lavoro.descrizione && (
                            <p className="text-xs text-gray-500 mt-0.5 ml-6 truncate">{lavoro.descrizione}</p>
                          )}
                        </div>
                        <div className="text-right shrink-0">
                          <p className="text-sm font-semibold text-primary-700 currency">
                            {formatCurrency(lavoroTotal)}
                          </p>
                          <p className="text-xs text-gray-500">
                            {lavoroVociSelected.length}/{lavoro.voci.length} voci
                          </p>
                        </div>
                      </div>

                      {/* Expanded voci */}
                      {isExpanded && lavoro.selected && (
                        <div className="border-t border-gray-100 divide-y divide-gray-50">
                          {lavoro.voci.map((voce, voceIdx) => (
                            <div
                              key={voceIdx}
                              className={`px-4 py-2.5 ${voce.selected ? '' : 'opacity-50'}`}
                            >
                              <div className="flex items-start gap-3">
                                <input
                                  type="checkbox"
                                  checked={voce.selected}
                                  onChange={() => toggleVoce(lavoroIdx, voceIdx)}
                                  className="mt-0.5 h-3.5 w-3.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                />
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="text-xs font-medium bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded">
                                      {voce.categoria}
                                    </span>
                                    <span className="text-xs text-gray-900 truncate">{voce.voce}</span>
                                  </div>
                                  {voce.note && (
                                    <p className="text-xs text-gray-500 mb-1.5">{voce.note}</p>
                                  )}
                                  <div className="grid grid-cols-3 gap-2">
                                    <div>
                                      <label className="text-xs text-gray-400">Min</label>
                                      <input
                                        type="number"
                                        value={voce.costoMin}
                                        onChange={(e) => updateVoceCost(lavoroIdx, voceIdx, 'costoMin', e.target.value)}
                                        className="w-full text-xs py-1"
                                        min="0"
                                        step="0.01"
                                        disabled={!voce.selected}
                                      />
                                    </div>
                                    <div>
                                      <label className="text-xs text-gray-400">Med</label>
                                      <input
                                        type="number"
                                        value={voce.costoMed}
                                        onChange={(e) => updateVoceCost(lavoroIdx, voceIdx, 'costoMed', e.target.value)}
                                        className="w-full text-xs py-1"
                                        min="0"
                                        step="0.01"
                                        disabled={!voce.selected}
                                      />
                                    </div>
                                    <div>
                                      <label className="text-xs text-gray-400">Max</label>
                                      <input
                                        type="number"
                                        value={voce.costoMax}
                                        onChange={(e) => updateVoceCost(lavoroIdx, voceIdx, 'costoMax', e.target.value)}
                                        className="w-full text-xs py-1"
                                        min="0"
                                        step="0.01"
                                        disabled={!voce.selected}
                                      />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex gap-3 pt-6">
            {step === 0 && (
              <>
                <button type="button" onClick={onClose} className="btn-secondary flex-1">
                  Annulla
                </button>
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="btn-primary flex-1"
                  disabled={!fileData}
                >
                  <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                  Estrai Lavori
                </button>
              </>
            )}
            {step === 2 && (
              <>
                <button
                  type="button"
                  onClick={() => {
                    extractMutation.reset();
                    setStep(0);
                  }}
                  className="btn-secondary flex-1"
                >
                  Ricomincia
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  className="btn-primary flex-1"
                  disabled={allSelectedVoci.length === 0 || importMutation.isPending}
                >
                  {importMutation.isPending
                    ? 'Importazione...'
                    : `Importa ${selectedLavori.filter((l) => l.voci.some((v) => v.selected)).length} Lavori (${allSelectedVoci.length} voci)`}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
